import {
  DatePicker,
  Input,
  SelectWithCheckbox,
  SelectWithRadio,
  TextArea,
} from "@/components/form";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getClientFilterService } from "@/service/client.service";
import { selectUser } from "@/store/slices/authSlice";
import { selectProjectLoading } from "@/store/slices/projectSlice";
import { fetchUserOptions, selectUserOptions } from "@/store/slices/userSlice";
import { ClientFilterListResponse } from "@/types/client.model";
import { ProjectList } from "@/types/project.model";
import { ROLES } from "@/utils/constants";
import {
  HOURS_DECIMAL_PATTERN,
  HOURS_DECIMAL_PATTERN_MESSAGE,
  sanitizeDecimalHoursInput,
} from "@/utils/hoursInput";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";

export interface ProjectFormData {
  id?: string;
  name: string;
  clientId: string;
  orgId?: number; // org_id for the selected client
  clientName?: string;
  // org_name?: string;
  email?: string;
  startDate: string;
  endDate: string;
  estimatedHours: number | null;
  assignedTo: string[];
  notes?: string;
  status?: "active" | "completed";
  projectType?: "billable" | "non-billable" | "block of time";
}

interface ProjectFormProps {
  onSubmit: (data: ProjectFormData) => void;
  initialData?: ProjectList;
  onCancel: () => void;
  viewMode?: boolean;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
  viewMode = false,
}) => {
  // Get clients from Redux store

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // const clients = useAppSelector(selectClientOptions);

  const [clients, setClients] = useState<ClientFilterListResponse[]>([]);
  const initialFetchDone = useRef(false);

  const users = useAppSelector(selectUserOptions);
  const isLoading = useAppSelector(selectProjectLoading);

  // Set default values
  let defaultValues: Partial<ProjectFormData> = {
    name: "",
    clientId: "",
    orgId: undefined,
    email: "",
    startDate: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
    endDate: "",
    estimatedHours: null,
    assignedTo: [],
    notes: "",
    status: "active",
    projectType: "non-billable",
  };

  // If initialData is provided, use it for defaultValues
  if (initialData) {
    console.log("initialData", initialData);
    defaultValues = {
      id: initialData.id,
      name: initialData.name,
      clientId: initialData.client.client_id,
      orgId: (initialData as any).client?.client_id || undefined, // org_id from existing project
      email: initialData.email || "",
      startDate: moment(initialData.start_date, "DD/MM/YYYY").format(
        "YYYY-MM-DD"
      ),
      endDate: initialData.end_date
        ? moment(initialData.end_date, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      estimatedHours: initialData.tracker?.total_esti_hrs as number,
      assignedTo: initialData.assignees?.map((user) => user.id as string) || [],
      notes: initialData.notes || "",
      status: initialData.status,
      projectType: (initialData as any)?.project_type || "non-billable",
    };
  }

  // Initialize form
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    clearErrors,
    trigger,
  } = useForm<ProjectFormData>({
    defaultValues,
    mode: "all",
    criteriaMode: "firstError",
    reValidateMode: "onChange",
  });

  // Track if the form has been modified
  const [isFormModified, setIsFormModified] = useState(false);

  const user = useAppSelector(selectUser);

  const isAdmin = user?.role_code === ROLES.ADMIN;

  // Watch all form values to detect changes
  const formValues = watch();
  const selectedProjectType = watch("projectType");
  const isBlockOfTime = selectedProjectType === "block of time";

  // Trigger validation immediately when the form is mounted in edit mode
  useEffect(() => {
    if (initialData && !viewMode) {
      // Use setTimeout to ensure validation runs after the component has fully mounted
      // This helps ensure all field registrations are complete
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [initialData, trigger, viewMode]);

  // Status options
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
  ];

  const projectTypeOptions = [
    { value: "billable", label: "Billable" },
    { value: "non-billable", label: "Non-billable" },
    { value: "block of time", label: "Block of time" },
  ];

  // Transform clients data to match the expected format for SelectWithRadio
  const clientOptions =
    clients?.map((client) => ({
      value: client?.id,
      // label: client.user?.full_name,
      label: client?.org_name,
    })) || [];

  const employeeOptions = !isAdmin
    ? initialData?.assignees?.map((user) => ({
      value: user.id,
      label: user.first_name + " " + user.last_name,
    })) || []
    : users?.map((user) => ({
      value: user.id,
      label: user.full_name,
    })) || [];

  useEffect(() => {
    // Only fetch if we haven't done the initial fetch yet
    if (!initialFetchDone.current) {
      const fetchClients = async () => {
        try {
          const { data } = await getClientFilterService({
            showAll: viewMode || Boolean(initialData),
          });
          setClients(data);
        } catch (error) {
          console.error("Error fetching clients:", error);
        }
      };

      fetchClients();

      if (isAdmin) {
        dispatch(
          fetchUserOptions({ showAll: viewMode || Boolean(initialData) })
        );
      }

      // Mark that we've done the initial fetch
      initialFetchDone.current = true;
    }
  }, [dispatch, viewMode, initialData, isAdmin]);

  // In create mode, preselect all available assignees once options are loaded.
  useEffect(() => {
    if (viewMode || initialData) return;
    const allAssigneeIds = employeeOptions.map((opt) => opt.value).filter(Boolean);
    if (!allAssigneeIds.length) return;
    const current = watch("assignedTo") || [];
    if (!current.length) {
      setValue("assignedTo", allAssigneeIds as any, { shouldValidate: true });
      trigger("assignedTo");
    }
  }, [employeeOptions, initialData, viewMode, setValue, trigger, watch]);

  // Check if form values have changed from initial data
  useEffect(() => {
    if (initialData) {
      // Compare current form values with initial data
      const hasChanged =
        formValues.name !== initialData.name ||
        formValues.clientId !== initialData.client.id ||
        formValues.email !== (initialData.email || "") ||
        formValues.startDate !==
        moment(initialData.start_date, "DD/MM/YYYY").format("YYYY-MM-DD") ||
        formValues.endDate !==
        (initialData.end_date ? moment(initialData.end_date, "DD/MM/YYYY").format("YYYY-MM-DD") : "") ||
        formValues.estimatedHours !== initialData.tracker?.total_esti_hrs ||
        JSON.stringify(formValues.assignedTo) !==
        JSON.stringify(
          initialData.assignees?.map((user) => user.id as string) || []
        ) ||
        formValues.notes !== (initialData.notes || "") ||
        formValues.status !== initialData.status;

      setIsFormModified(hasChanged);
    }
  }, [formValues, initialData]);

  const encodeToBase64 = (input: string): string => {
    try {
      if (
        typeof globalThis !== "undefined" &&
        (globalThis as any).TextEncoder &&
        (globalThis as any).btoa
      ) {
        const bytes = new TextEncoder().encode(input);
        let binary = "";
        for (let i = 0; i < bytes.length; i++)
          binary += String.fromCharCode(bytes[i]);
        return (globalThis as any).btoa(binary);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (Buffer as any).from(input, "utf-8").toString("base64");
    } catch {
      return "";
    }
  };

  const navigateToEmailWithEncodedParams = () => {
    const clientId = initialData?.client?.client_id || "";
    const projectId = initialData?.id || "";
    const payload = { c: clientId, p: projectId, ts: Date.now() };
    const json = JSON.stringify(payload);
    const b64 = encodeURIComponent(encodeToBase64(json));
    navigate(`/email?payload=${b64}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-5 px-4 sm:px-4 py-2 overflow-y-auto max-h-[calc(100vh-200px)]">
        {!viewMode && (
          <p className="text-[14px] text-[#6C6C6C] mb-2">
            <span className="text-[#DC2626]">*</span> indicates mandatory fields
          </p>
        )}

        {viewMode && watch("projectType") === "block of time" && (
          <div className="text-sm  text-[#1E1E1E]">
            Would you like to email this project’s report?{" "}
            <button
              type="button"
              className="text-[#6093A0] cursor-pointer underline"
              onClick={navigateToEmailWithEncodedParams}
            >
              Send email
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {/* Project Name */}
          <div>
            <Input
              label="Project name"
              id="name"
              autoFocus={!viewMode && !initialData}
              placeholder="Enter project name"
              readOnly={viewMode}
              error={errors.name?.message}
              required={true}
              maxLength={50}
              {...register("name", {
                required: !viewMode ? "Project name is required" : false,
              })}
            />
          </div>

          {/* Project Type */}
          <div>
            <SelectWithRadio
              showAbove={false}
              label="Project type"
              id="projectType"
              options={projectTypeOptions}
              value={watch("projectType") || ""}
              onChange={(value) => {
                const v = value as typeof selectedProjectType;
                setValue("projectType", v);
                // When moving away from Block of time, clear and disable End Date & Estimated Hours
                if (v !== "block of time") {
                  setValue("endDate", "");
                  setValue("estimatedHours", null as any);
                  clearErrors(["endDate", "estimatedHours"] as any);
                } else {
                  // Ensure validation runs when switching to Block of time
                  trigger(["endDate", "estimatedHours"] as any);
                }
                trigger("projectType");
              }}
              placeholder="Select project type"
              error={(errors as any).projectType?.message}
              required={true}
              disabled={viewMode || initialData}
            />
            <input
              type="hidden"
              {...register("projectType", {
                required: !viewMode ? "Project type is required" : false,
              })}
            />
          </div>

          {/* Organization Name */}
          <div>
            <SelectWithRadio
              showAbove={false}
              label="Organization name"
              id="clientId"
              options={clientOptions}
              value={watch("clientId") || ""}
              onChange={(value) => {
                setValue("clientId", value);
                trigger("clientId");

                // Find the selected client and set their email and org_id
                const selectedClient = clients.find(
                  (client) => client.id === value
                );
                if (selectedClient) {
                  // Set org_id from the selected client
                  if (selectedClient.id) {
                    setValue("orgId", Number(selectedClient.id));
                  }
                  if (selectedClient.user?.email) {
                    setValue("email", selectedClient.user.email);
                    trigger("email");
                  }
                }
              }}
              placeholder="Select organization name"
              error={errors.clientId?.message}
              required={true}
              disabled={viewMode}
            />
            <input
              type="hidden"
              {...register("clientId", {
                required: !viewMode ? "Client name is required" : false,
              })}
            />
          </div>

          {/* Email Address */}
          <div>
            <Input
              label="Email address"
              id="email"
              type="email"
              placeholder="Email address"
              readOnly={true}
              error={errors.email?.message}
              className={viewMode ? "text-[#6C6C6C]" : ""}
              maxLength={50}
              {...register("email", {
                // pattern: {
                //   value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                //   message: "Invalid email address",
                // },
              })}
            />
          </div>

          {/* Start Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DatePicker
              label="Start date"
              id="startDate"
              placeholder="Select start date"
              value={watch("startDate") || ""}
              onChange={(value) => {
                setValue("startDate", value);
                trigger("startDate");
              }}
              readOnly={viewMode}
              required={true}
              error={errors.startDate?.message}
            />
            <input
              type="hidden"
              {...register("startDate", {
                required: !viewMode ? "Start date is required" : false,
              })}
            />
            {!(initialData && !initialData.end_date) && (
              <>
                {(() => {
                  const end = watch("endDate") || "";
                  let isPast = false;
                  if (viewMode && end) {
                    const d = new Date(end);
                    if (!Number.isNaN(d.getTime())) {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      d.setHours(0, 0, 0, 0);
                      isPast = d < today;
                    }
                  }
                  return (
                    <DatePicker
                      label="End date"
                      id="endDate"
                      placeholder="Select end date"
                      minDate={watch("startDate")}
                      value={watch("endDate") || ""}
                      onChange={(value) => {
                        setValue("endDate", value);
                        trigger("endDate");
                      }}
                      readOnly={viewMode || !isBlockOfTime}
                      required={isBlockOfTime}
                      error={errors.endDate?.message}
                      labelClassName={isPast ? "text-[#DC2626]" : ""}
                    />
                  );
                })()}
                <input
                  type="hidden"
                  {...register("endDate", {
                    required:
                      !viewMode && isBlockOfTime
                        ? "End date is required"
                        : false,
                  })}
                />
              </>
            )}
          </div>
          {/* Estimated Hours */}
          <div
            className={
              !viewMode && !Boolean(initialData)
                ? "grid grid-cols-1 mb-4"
                : "grid grid-cols-2 gap-4"
            }
          >
            {!(
              viewMode &&
              initialData?.tracker?.total_esti_hrs == null &&
              initialData?.esti_hrs == null
            ) && (
                <Input
                  label="Estimated hours"
                  id="estimatedHours"
                  readOnly={viewMode || !isBlockOfTime}
                  placeholder="Enter number of hours"
                  error={errors.estimatedHours?.message}
                  required={isBlockOfTime}
                  maxLength={10}
                  onInput={sanitizeDecimalHoursInput}
                  {...register("estimatedHours", {
                    required:
                      !viewMode && isBlockOfTime
                        ? "Estimated hours is required"
                        : false,
                    pattern: {
                      value: HOURS_DECIMAL_PATTERN,
                      message: HOURS_DECIMAL_PATTERN_MESSAGE,
                    },
                  })}
                />
              )}
          </div>
        </div>

        {/* Utilized / Remaining / Scenario row */}
        {viewMode && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {(() => {
              const utilRaw = (initialData?.tracker?.util_hrs ??
                initialData?.util_hrs) as any;
              const estiRaw = (initialData?.tracker?.total_esti_hrs ??
                initialData?.esti_hrs) as any;
              const remRaw = (initialData?.tracker?.remi_hrs ??
                initialData?.remi_hrs) as any;
              const utilNum = Number.parseFloat(
                String(utilRaw ?? "0").toString()
              );
              const estiNum = Number.parseFloat(
                String(estiRaw ?? "0").toString()
              );
              const remNum = Number.parseFloat(
                String(remRaw ?? "0").toString()
              );
              const remNegative = !Number.isNaN(remNum) && remNum < 0;
              const utilDanger =
                (!Number.isNaN(utilNum) && utilNum > estiNum && estiNum > 0) ||
                remNegative;
              return (
                <Input
                  label="Utilized hours"
                  id="util_readonly_inline"
                  readOnly
                  className={utilDanger ? "text-[#DC2626]" : ""}
                  value={String(utilRaw ?? "")}
                />
              );
            })()}
            {(() => {
              const hasRem =
                initialData?.tracker?.remi_hrs != null ||
                initialData?.remi_hrs != null;
              if (!hasRem) return null;
              const remRaw = (initialData?.tracker?.remi_hrs ??
                initialData?.remi_hrs) as any;
              const remNum = Number.parseFloat(
                String(remRaw ?? "0").toString()
              );
              const remDanger = !Number.isNaN(remNum) && remNum < 0;
              return (
                <Input
                  label="Remaining hours"
                  id="rem_readonly_inline"
                  readOnly
                  className={remDanger ? "text-[#DC2626]" : ""}
                  value={String(remRaw ?? "")}
                />
              );
            })()}
            {(() => {
              const remRaw = (initialData?.tracker?.remi_hrs ??
                initialData?.remi_hrs) as any;
              const remNum = Number.parseFloat(
                String(remRaw ?? "0").toString()
              );
              const scenario =
                !Number.isNaN(remNum) && remNum < 0
                  ? "Out of hours"
                  : "Still have hours";
              return (
                <Input
                  label="Project scenario"
                  id="scenario_inline"
                  readOnly
                  value={scenario}
                />
              );
            })()}
          </div>
        )}

        <div className="mb-4">
          {/* Status - Only show for edit mode */}
          {initialData && (
            <SelectWithRadio
              label="Project Status"
              id="status"
              options={statusOptions}
              value={watch("status") || "Active"}
              onChange={(value) => setValue("status", value as any)}
              disabled={viewMode}
            />
          )}
        </div>
        {/* Assign Employee/Contractor */}

        <SelectWithCheckbox
          label="Assign employee/contractor"
          id="assignedTo"
          options={employeeOptions}
          value={watch("assignedTo") || []}
          onChange={(value) => {
            setValue("assignedTo", value as any);
            trigger("assignedTo");
          }}
          maxHeight="250px"
          placeholder="Select employee/contractor"
          error={errors.assignedTo?.message}
          required={true}
          disabled={viewMode}
        />
        <input
          type="hidden"
          {...register("assignedTo", {
            validate: (value) =>
              !viewMode
                ? (value && value.length > 0) ||
                "Please assign at least one employee/contractor"
                : true,
          })}
        />

        {/* Notes */}
        <div className="mt-4">
          <TextArea
            label="Notes"
            id="notes"
            readOnly={viewMode}
            resizable={false}
            {...register("notes")}
          />
        </div>
      </div>

      {!viewMode && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[14px] w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[4px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed flex items-center justify-center"
            disabled={!isValid || isLoading || (initialData && !isFormModified)}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {initialData ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>{initialData ? "Update" : "Add Project"}</>
            )}
          </button>
        </div>
      )}
    </form>
  );
};

export default ProjectForm;
